import { ReactElement, useState, useCallback } from 'react';

import { Timestamp } from 'firebase/firestore';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { useAutoSave } from '../../stateHooks/useAutoSave';
import Grouping, { CRITERION_CATEGORIES, Criterion, CriterionCategory } from '../../types/Grouping';
import { HANDICAP_LABELS } from '../../utils/handicapUtils';
import GratiGroupingNameEditor from '../common/GratiGroupingNameEditor';

const CATEGORY_LABELS: Record<CriterionCategory, string> = {
  [CRITERION_CATEGORIES.AGE]: 'Age',
  [CRITERION_CATEGORIES.GENDER]: 'Gender',
  [CRITERION_CATEGORIES.SKILL]: 'Skill',
  [CRITERION_CATEGORIES.COLLEGIATE]: 'Collegiate',
  [CRITERION_CATEGORIES.HANDICAP]: 'Handicap',
};

function formatCriterionSummary(c: Criterion): string {
  switch (c.category) {
    case CRITERION_CATEGORIES.GENDER:
      return { MALE: 'Men', FEMALE: 'Women', MIXED: 'Mixed' }[c.value ?? ''] ?? c.value ?? '';
    case CRITERION_CATEGORIES.SKILL:
      return { PRO: 'Pro', AMATEUR: 'Amateur' }[c.value ?? ''] ?? c.value ?? '';
    case CRITERION_CATEGORIES.COLLEGIATE:
      return { D1: 'Division I', D2: 'Division II', D3: 'Division III' }[c.value ?? ''] ?? c.value ?? '';
    case CRITERION_CATEGORIES.AGE: {
      if (c.minAge != null && c.maxAge != null) return `Ages ${c.minAge}–${c.maxAge}`;
      if (c.minAge != null) return `${c.minAge}+`;
      if (c.maxAge != null) return `Under ${c.maxAge + 1}`;
      return '';
    }
    case CRITERION_CATEGORIES.HANDICAP: {
      if (c.minHandicap != null && c.maxHandicap != null) return `${HANDICAP_LABELS.INDEX_SHORT} ${c.minHandicap}–${c.maxHandicap}`;
      if (c.minHandicap != null) return `${HANDICAP_LABELS.INDEX_SHORT} ${c.minHandicap}+`;
      if (c.maxHandicap != null) return `${HANDICAP_LABELS.INDEX_SHORT} up to ${c.maxHandicap}`;
      return '';
    }
    default: return '';
  }
}

function autoName(c: Criterion): string {
  const summary = formatCriterionSummary(c);
  return summary ? `${CATEGORY_LABELS[c.category]}: ${summary}` : CATEGORY_LABELS[c.category];
}

function isCriterionValid(category: CriterionCategory, form: Partial<Criterion>): boolean {
  if (!category) return false;
  if (
    category === CRITERION_CATEGORIES.GENDER ||
    category === CRITERION_CATEGORIES.SKILL ||
    category === CRITERION_CATEGORIES.COLLEGIATE
  ) {
    return !!form.value;
  }
  return true;
}

const timestampToDateString = (ts?: Timestamp) =>
  ts ? ts.toDate().toISOString().split('T')[0] : '';

const dateStringToTimestamp = (s: string): Timestamp | undefined =>
  s ? Timestamp.fromDate(new Date(s)) : undefined;

type CriterionRowProps = {
  form: Partial<Criterion>;
  category: CriterionCategory | '';
  onCategoryChange: (cat: CriterionCategory | '') => void;
  onFormChange: (updates: Partial<Criterion>) => void;
  onRemove?: () => void;
  isExisting?: boolean;
  summary?: string;
};

function CriterionRow({ form, category, onCategoryChange, onFormChange, onRemove, isExisting, summary }: CriterionRowProps): ReactElement {
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{
        alignItems: "center"
      }}>
        <FormControl sx={{ minWidth: '9rem' }} size="small">
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={category}
            onChange={(e) => onCategoryChange(e.target.value as CriterionCategory | '')}
            disabled={isExisting}
          >
            {Object.values(CRITERION_CATEGORIES).map((cat) => (
              <MenuItem key={cat} value={cat}>{CATEGORY_LABELS[cat]}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {(category === CRITERION_CATEGORIES.GENDER) && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={form.value ?? null}
            onChange={(_, val) => { if (val !== null) onFormChange({ value: val }); }}
          >
            <ToggleButton value="MALE">Men</ToggleButton>
            <ToggleButton value="FEMALE">Women</ToggleButton>
            <ToggleButton value="MIXED">Mixed</ToggleButton>
          </ToggleButtonGroup>
        )}
        {(category === CRITERION_CATEGORIES.SKILL) && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={form.value ?? null}
            onChange={(_, val) => { if (val !== null) onFormChange({ value: val }); }}
          >
            <ToggleButton value="PRO">Pro</ToggleButton>
            <ToggleButton value="AMATEUR">Amateur</ToggleButton>
          </ToggleButtonGroup>
        )}
        {(category === CRITERION_CATEGORIES.COLLEGIATE) && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={form.value ?? null}
            onChange={(_, val) => { if (val !== null) onFormChange({ value: val }); }}
          >
            <ToggleButton value="D1">D-I</ToggleButton>
            <ToggleButton value="D2">D-II</ToggleButton>
            <ToggleButton value="D3">D-III</ToggleButton>
          </ToggleButtonGroup>
        )}

        {onRemove && (
          <IconButton size="small" aria-label="remove criterion" onClick={onRemove} sx={{ ml: 'auto' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      {(category === CRITERION_CATEGORIES.AGE) && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: "flex-start",
            flexWrap: "wrap"
          }}>
          <TextField size="small" label="Min age" type="number" sx={{ width: '7rem' }}
            value={form.minAge ?? ''}
            onChange={(e) => onFormChange({ minAge: e.target.value ? Number(e.target.value) : undefined })}
          />
          <TextField size="small" label="Max age" type="number" sx={{ width: '7rem' }}
            value={form.maxAge ?? ''}
            onChange={(e) => onFormChange({ maxAge: e.target.value ? Number(e.target.value) : undefined })}
          />
          <TextField size="small" label="Age as of date" type="date" sx={{ width: '11rem' }}
            slotProps={{ inputLabel: { shrink: true } }}
            value={timestampToDateString(form.ageCalculationDate as Timestamp | undefined)}
            onChange={(e) => onFormChange({ ageCalculationDate: dateStringToTimestamp(e.target.value) })}
          />
        </Stack>
      )}
      {(category === CRITERION_CATEGORIES.HANDICAP) && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: "flex-start",
            flexWrap: "wrap"
          }}>
          <TextField size="small" label={`Min ${HANDICAP_LABELS.INDEX_SHORT}`} type="number" sx={{ width: '9rem' }}
            value={form.minHandicap ?? ''}
            onChange={(e) => onFormChange({ minHandicap: e.target.value ? Number(e.target.value) : undefined })}
          />
          <TextField size="small" label={`Max ${HANDICAP_LABELS.INDEX_SHORT}`} type="number" sx={{ width: '9rem' }}
            value={form.maxHandicap ?? ''}
            onChange={(e) => onFormChange({ maxHandicap: e.target.value ? Number(e.target.value) : undefined })}
          />
          <TextField size="small" label={`${HANDICAP_LABELS.INDEX_SHORT} as of date`} type="date" sx={{ width: '11rem' }}
            slotProps={{ inputLabel: { shrink: true } }}
            value={timestampToDateString(form.handicapCalculationDate as Timestamp | undefined)}
            onChange={(e) => onFormChange({ handicapCalculationDate: dateStringToTimestamp(e.target.value) })}
          />
        </Stack>
      )}
      {isExisting && summary && (
        <Typography variant="bodySmall" sx={{
          color: "text.secondary"
        }}>{summary}</Typography>
      )}
    </Stack>
  );
}

export interface DivisionEditorProps {
  division: Grouping;
  isNew?: boolean;
  handleCreate?: (division: Grouping) => void;
  updateDivision?: (changes: Partial<Grouping>) => Promise<void>;
  onDivisionChange?: (division: Grouping) => void;
}

export default function DivisionEditor(props: DivisionEditorProps): ReactElement {
  const { isNew = false, updateDivision, onDivisionChange } = props;

  const [division, setDivision] = useState<Grouping>(props.division);
  const [newCategory, setNewCategory] = useState<CriterionCategory | ''>('');
  const [newForm, setNewForm] = useState<Partial<Criterion>>({});

  const { queueChange } = useAutoSave<Grouping>({
    updateFn: updateDivision,
    itemId: division.itemId,
    additionalFields: { leagueId: division.leagueId },
    enabled: !isNew && !!updateDivision,
  });

  const handleFieldChange = useCallback(<K extends keyof Grouping>(field: K, value: Grouping[K]) => {
    setDivision((prev) => {
      const updated = { ...prev, [field]: value };
      onDivisionChange?.(updated);
      return updated;
    });
    queueChange(field, value);
  }, [queueChange, onDivisionChange]);

  const handleNameChange = useCallback((field: 'name' | 'symbol' | 'backgroundColor', value: string) => {
    handleFieldChange(field as keyof Grouping, value as never);
  }, [handleFieldChange]);

  const handleAddCriterion = useCallback(() => {
    if (!newCategory) return;
    const criterion: Criterion = {
      ...newForm,
      category: newCategory,
      name: autoName({ ...newForm, category: newCategory } as Criterion),
    };
    handleFieldChange('criteria', [...(division.criteria ?? []), criterion]);
    setNewCategory('');
    setNewForm({});
  }, [newCategory, newForm, division.criteria, handleFieldChange]);

  const handleRemoveCriterion = useCallback((idx: number) => {
    handleFieldChange('criteria', (division.criteria ?? []).filter((_, i) => i !== idx));
  }, [division.criteria, handleFieldChange]);

  return (
    <Stack spacing={2} sx={{ maxWidth: 'sm' }}>
      {/* Color / symbol / name */}
      <GratiGroupingNameEditor
        name={division.name}
        symbol={division.symbol}
        backgroundColor={division.backgroundColor}
        nameLabel="Division name"
        nameRequired
        onChange={handleNameChange}
      />

      {/* Description */}
      <TextField
        label="Description"
        multiline
        minRows={2}
        value={division.description}
        onChange={({ target }) => handleFieldChange('description', target.value)}
        sx={{ maxWidth: 'sm' }}
      />

      {/* Existing criteria */}
      {(division.criteria ?? []).length > 0 && (
        <Stack spacing={2}>
          <Typography variant="labelLarge">Criteria</Typography>
          {(division.criteria ?? []).map((c, idx) => (
            <CriterionRow
              key={idx}
              isExisting
              category={c.category}
              form={c}
              onCategoryChange={() => {}}
              onFormChange={() => {}}
              onRemove={() => handleRemoveCriterion(idx)}
              summary={formatCriterionSummary(c)}
            />
          ))}
        </Stack>
      )}

      {/* Add criterion */}
      <Stack spacing={1.5}>
        <Typography variant="labelLarge">
          {(division.criteria ?? []).length === 0 ? 'Criteria' : ''}
        </Typography>
        <CriterionRow
          category={newCategory}
          form={newForm}
          onCategoryChange={(cat) => { setNewCategory(cat); setNewForm({}); }}
          onFormChange={(updates) => setNewForm((f) => ({ ...f, ...updates }))}
        />
        {newCategory && (
          <Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCriterion}
              disabled={!isCriterionValid(newCategory as CriterionCategory, newForm)}
            >
              {(division.criteria ?? []).length === 0 ? 'Add' : 'Add additional criterion'}
            </Button>
          </Box>
        )}
        {!newCategory && (division.criteria ?? []).length > 0 && (
          <Box>
            <Button
              size="small"
              variant="text"
              startIcon={<AddIcon />}
              onClick={() => setNewCategory(CRITERION_CATEGORIES.AGE)}
            >
              Additional criterion
            </Button>
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
